import { createTransportFromMessagePort, createRPC } from 'rpc-anywhere';
import { required, object, string, optional, parse } from 'valibot';

const IFRAME_MSG_KEY = "[iframe-transport]";
const IFRAME_READY_MSG = "[iframe-transport-ready]";
async function portReadyPromise(port) {
  return new Promise((resolve) => {
    port.addEventListener("message", function ready(event) {
      if (event.data === IFRAME_READY_MSG) {
        port.removeEventListener("message", ready);
        resolve();
      }
    });
  });
}
async function createIframeTransport(iframe, options = {}) {
  const channel = new MessageChannel();
  const { port1, port2 } = channel;
  port1.start();
  const transport = createTransportFromMessagePort(port1, options);
  const readyPromise = portReadyPromise(port1);
  if (!iframe.contentWindow)
    throw new Error("Unexpected iframe state");
  iframe.contentWindow.postMessage(
    { [IFRAME_MSG_KEY]: options.transportId ?? "default" },
    options.targetOrigin ?? "*",
    [port2]
  );
  await readyPromise;
  return transport;
}

var __accessCheck$3 = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet$3 = (obj, member, getter) => {
  __accessCheck$3(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd$3 = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet$3 = (obj, member, value, setter) => {
  __accessCheck$3(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod$2 = (obj, member, method) => {
  __accessCheck$3(obj, member, "access private method");
  return method;
};
var _client$2, _type, _rpc$2, _appearance$2, _waitForIframeAbortController$2, _createIframe$2, createIframe_fn$2, _waitForIframe$2, waitForIframe_fn$2, _createRPC$2, createRPC_fn$2, _loadUser$2, loadUser_fn$2;
var WalletType = /* @__PURE__ */ ((WalletType2) => {
  WalletType2["Sent"] = "sent";
  WalletType2["Default"] = "default";
  return WalletType2;
})(WalletType || {});
class Wallet {
  constructor(type, client, appearance = null) {
    __privateAdd$3(this, _createIframe$2);
    __privateAdd$3(this, _waitForIframe$2);
    __privateAdd$3(this, _createRPC$2);
    __privateAdd$3(this, _loadUser$2);
    __privateAdd$3(this, _client$2, void 0);
    __privateAdd$3(this, _type, void 0);
    __privateAdd$3(this, _rpc$2, null);
    __privateAdd$3(this, _appearance$2, void 0);
    __privateAdd$3(this, _waitForIframeAbortController$2, null);
    __privateSet$3(this, _type, type);
    __privateSet$3(this, _appearance$2, appearance);
    __privateSet$3(this, _client$2, client);
  }
  mount(selector) {
    __privateGet$3(this, _waitForIframeAbortController$2)?.abort();
    const iframe = __privateMethod$2(this, _createIframe$2, createIframe_fn$2).call(this);
    if (typeof selector === "string") {
      document.querySelector(selector)?.appendChild(iframe);
    } else {
      selector.appendChild(iframe);
    }
    return __privateMethod$2(this, _waitForIframe$2, waitForIframe_fn$2).call(this, iframe);
  }
  listChains() {
    return __privateGet$3(this, _rpc$2)?.proxy.request.listChains() || Promise.resolve([]);
  }
  getBalance(params) {
    return __privateGet$3(this, _rpc$2)?.proxy.request.getBalance(params);
  }
  sendOtp() {
    return __privateGet$3(this, _rpc$2)?.proxy.request.sendOtp();
  }
  verifyOtp(data) {
    return __privateGet$3(this, _rpc$2)?.proxy.request.verifyOtp(data);
  }
  getEstimateGas(data) {
    return __privateGet$3(this, _rpc$2)?.proxy.request.getEstimateGas(data);
  }
  withdraw(data) {
    return __privateGet$3(this, _rpc$2)?.proxy.request.withdraw(data);
  }
  isLoggedIn() {
    return __privateGet$3(this, _rpc$2)?.proxy.request.isLoggedIn();
  }
  is2faEnabled() {
    return __privateGet$3(this, _rpc$2)?.proxy.request.is2faEnabled();
  }
  dispose() {
    __privateSet$3(this, _rpc$2, null);
    __privateSet$3(this, _client$2, null);
    __privateSet$3(this, _appearance$2, null);
    __privateGet$3(this, _waitForIframeAbortController$2)?.abort();
    __privateSet$3(this, _waitForIframeAbortController$2, null);
  }
}
_client$2 = new WeakMap();
_type = new WeakMap();
_rpc$2 = new WeakMap();
_appearance$2 = new WeakMap();
_waitForIframeAbortController$2 = new WeakMap();
_createIframe$2 = new WeakSet();
createIframe_fn$2 = function() {
  const iframe = document.createElement("iframe");
  iframe.src = `${__privateGet$3(this, _client$2)?.origin}/wallet?type=${__privateGet$3(this, _type)}`;
  iframe.width = __privateGet$3(this, _appearance$2)?.width || "100%";
  iframe.height = __privateGet$3(this, _appearance$2)?.height || "100%";
  return iframe;
};
_waitForIframe$2 = new WeakSet();
waitForIframe_fn$2 = function(iframe) {
  __privateSet$3(this, _waitForIframeAbortController$2, new AbortController());
  const controller = __privateGet$3(this, _waitForIframeAbortController$2);
  return new Promise((resolve, reject) => {
    iframe.addEventListener("load", () => {
      const readyInterval = setInterval(() => {
        iframe.contentWindow?.postMessage("ready", "*");
      });
      controller.signal.addEventListener("abort", () => {
        clearInterval(readyInterval);
      });
      window.addEventListener("message", async (event) => {
        if (event.data === "ready") {
          try {
            window.clearInterval(readyInterval);
            __privateSet$3(this, _rpc$2, await __privateMethod$2(this, _createRPC$2, createRPC_fn$2).call(this, iframe));
            await __privateMethod$2(this, _loadUser$2, loadUser_fn$2).call(this);
            iframe.contentWindow?.postMessage({
              type: "appearance",
              appearance: __privateGet$3(this, _appearance$2)
            }, "*");
            resolve();
          } catch (e) {
            reject(e);
          }
          controller.abort();
        }
      }, { signal: controller.signal });
    });
  });
};
_createRPC$2 = new WeakSet();
createRPC_fn$2 = async function(iframe) {
  return createRPC({
    transport: await createIframeTransport(iframe, {
      transportId: "wallet"
    }),
    maxRequestTime: Infinity
  });
};
_loadUser$2 = new WeakSet();
loadUser_fn$2 = async function() {
  return await __privateGet$3(this, _rpc$2).proxy.request.loadUser({
    token: __privateGet$3(this, _client$2).token
  });
};

var __accessCheck$2 = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet$2 = (obj, member, getter) => {
  __accessCheck$2(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd$2 = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet$2 = (obj, member, value, setter) => {
  __accessCheck$2(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var _token, _origin;
const ClientSchema = required(object({
  token: string(),
  origin: optional(string())
}), ["token"]);
const _Client = class _Client {
  constructor(params) {
    __privateAdd$2(this, _token, void 0);
    __privateAdd$2(this, _origin, void 0);
    __privateSet$2(this, _token, params.token);
    __privateSet$2(this, _origin, params.origin || "https://sdk.chat3.one");
  }
  static create(params) {
    params = parse(ClientSchema, params);
    return new _Client(params);
  }
  get token() {
    return __privateGet$2(this, _token);
  }
  get origin() {
    return __privateGet$2(this, _origin);
  }
};
_token = new WeakMap();
_origin = new WeakMap();
let Client = _Client;

var __accessCheck$1 = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet$1 = (obj, member, getter) => {
  __accessCheck$1(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd$1 = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet$1 = (obj, member, value, setter) => {
  __accessCheck$1(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod$1 = (obj, member, method) => {
  __accessCheck$1(obj, member, "access private method");
  return method;
};
var _client$1, _rpc$1, _appearance$1, _waitForIframeAbortController$1, _spinId, _createIframe$1, createIframe_fn$1, _waitForIframe$1, waitForIframe_fn$1, _createRPC$1, createRPC_fn$1, _loadUser$1, loadUser_fn$1;
class Spin {
  constructor(spinId, client, appearance = null) {
    __privateAdd$1(this, _createIframe$1);
    __privateAdd$1(this, _waitForIframe$1);
    __privateAdd$1(this, _createRPC$1);
    __privateAdd$1(this, _loadUser$1);
    __privateAdd$1(this, _client$1, void 0);
    __privateAdd$1(this, _rpc$1, null);
    __privateAdd$1(this, _appearance$1, void 0);
    __privateAdd$1(this, _waitForIframeAbortController$1, null);
    __privateAdd$1(this, _spinId, void 0);
    __privateSet$1(this, _appearance$1, appearance);
    __privateSet$1(this, _client$1, client);
    __privateSet$1(this, _spinId, spinId);
  }
  mount(selector) {
    const iframe = __privateMethod$1(this, _createIframe$1, createIframe_fn$1).call(this);
    if (typeof selector === "string") {
      document.querySelector(selector)?.appendChild(iframe);
    } else {
      selector.appendChild(iframe);
    }
    return __privateMethod$1(this, _waitForIframe$1, waitForIframe_fn$1).call(this, iframe);
  }
  getUserSpin() {
    return __privateGet$1(this, _rpc$1).proxy.request.getUserSpin({ token: __privateGet$1(this, _client$1).token, spinId: __privateGet$1(this, _spinId) });
  }
  dispose() {
    __privateSet$1(this, _client$1, null);
    __privateSet$1(this, _appearance$1, null);
  }
}
_client$1 = new WeakMap();
_rpc$1 = new WeakMap();
_appearance$1 = new WeakMap();
_waitForIframeAbortController$1 = new WeakMap();
_spinId = new WeakMap();
_createIframe$1 = new WeakSet();
createIframe_fn$1 = function() {
  const iframe = document.createElement("iframe");
  iframe.src = `${__privateGet$1(this, _client$1)?.origin}/spin?spinId=${__privateGet$1(this, _spinId)}`;
  iframe.width = __privateGet$1(this, _appearance$1)?.width || "100%";
  iframe.height = __privateGet$1(this, _appearance$1)?.height || "100%";
  return iframe;
};
_waitForIframe$1 = new WeakSet();
waitForIframe_fn$1 = function(iframe) {
  __privateSet$1(this, _waitForIframeAbortController$1, new AbortController());
  const controller = __privateGet$1(this, _waitForIframeAbortController$1);
  return new Promise((resolve, reject) => {
    iframe.addEventListener("load", () => {
      const readyInterval = setInterval(() => {
        iframe.contentWindow?.postMessage("ready", "*");
      });
      controller.signal.addEventListener("abort", () => {
        clearInterval(readyInterval);
      });
      window.addEventListener("message", async (event) => {
        if (event.data === "ready") {
          try {
            window.clearInterval(readyInterval);
            __privateSet$1(this, _rpc$1, await __privateMethod$1(this, _createRPC$1, createRPC_fn$1).call(this, iframe));
            await __privateMethod$1(this, _loadUser$1, loadUser_fn$1).call(this);
            iframe.contentWindow?.postMessage({
              type: "appearance",
              appearance: __privateGet$1(this, _appearance$1)
            }, "*");
            resolve();
          } catch (e) {
            reject(e);
          }
          controller.abort();
        }
      }, { signal: controller.signal });
    });
  });
};
_createRPC$1 = new WeakSet();
createRPC_fn$1 = async function(iframe) {
  return createRPC({
    transport: await createIframeTransport(iframe, {
      transportId: "spin"
    }),
    maxRequestTime: Infinity
  });
};
_loadUser$1 = new WeakSet();
loadUser_fn$1 = async function() {
  return await __privateGet$1(this, _rpc$1).proxy.request.loadUser({
    token: __privateGet$1(this, _client$1).token
  });
};

var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};
var _client, _rpc, _appearance, _waitForIframeAbortController, _createIframe, createIframe_fn, _waitForIframe, waitForIframe_fn, _createRPC, createRPC_fn, _loadUser, loadUser_fn;
class Setup2fa {
  constructor(client, appearance = null) {
    __privateAdd(this, _createIframe);
    __privateAdd(this, _waitForIframe);
    __privateAdd(this, _createRPC);
    __privateAdd(this, _loadUser);
    __privateAdd(this, _client, void 0);
    __privateAdd(this, _rpc, null);
    __privateAdd(this, _appearance, void 0);
    __privateAdd(this, _waitForIframeAbortController, null);
    __privateSet(this, _appearance, appearance);
    __privateSet(this, _client, client);
  }
  mount(selector) {
    __privateGet(this, _waitForIframeAbortController)?.abort();
    const iframe = __privateMethod(this, _createIframe, createIframe_fn).call(this);
    if (typeof selector === "string") {
      document.querySelector(selector)?.appendChild(iframe);
    } else {
      selector.appendChild(iframe);
    }
    return __privateMethod(this, _waitForIframe, waitForIframe_fn).call(this, iframe);
  }
  onSubmit(cb) {
    const rpc = __privateGet(this, _rpc);
    rpc.addMessageListener("setup2fa", cb);
    return () => {
      rpc.removeMessageListener("setup2fa", cb);
    };
  }
}
_client = new WeakMap();
_rpc = new WeakMap();
_appearance = new WeakMap();
_waitForIframeAbortController = new WeakMap();
_createIframe = new WeakSet();
createIframe_fn = function() {
  const iframe = document.createElement("iframe");
  iframe.src = `${__privateGet(this, _client)?.origin}/setup2fa`;
  iframe.width = __privateGet(this, _appearance)?.width || "100%";
  iframe.height = __privateGet(this, _appearance)?.height || "100%";
  return iframe;
};
_waitForIframe = new WeakSet();
waitForIframe_fn = function(iframe) {
  __privateSet(this, _waitForIframeAbortController, new AbortController());
  const controller = __privateGet(this, _waitForIframeAbortController);
  return new Promise((resolve, reject) => {
    iframe.addEventListener("load", () => {
      const readyInterval = setInterval(() => {
        iframe.contentWindow?.postMessage("ready", "*");
      });
      controller.signal.addEventListener("abort", () => {
        clearInterval(readyInterval);
      });
      window.addEventListener("message", async (event) => {
        if (event.data === "ready") {
          try {
            window.clearInterval(readyInterval);
            __privateSet(this, _rpc, await __privateMethod(this, _createRPC, createRPC_fn).call(this, iframe));
            await __privateMethod(this, _loadUser, loadUser_fn).call(this);
            iframe.contentWindow?.postMessage({
              type: "appearance",
              appearance: __privateGet(this, _appearance)
            }, "*");
            resolve();
          } catch (e) {
            reject(e);
          }
          controller.abort();
        }
      }, { signal: controller.signal });
    });
  });
};
_createRPC = new WeakSet();
createRPC_fn = async function(iframe) {
  return createRPC({
    transport: await createIframeTransport(iframe, {
      transportId: "setup2fa"
    }),
    maxRequestTime: Infinity
  });
};
_loadUser = new WeakSet();
loadUser_fn = async function() {
  return await __privateGet(this, _rpc).proxy.request.loadUser({
    token: __privateGet(this, _client).token
  });
};

export { Client, Setup2fa, Spin, Wallet, WalletType };
